import { isArray } from "underscore";

/**
 * @function
 * @summary Pure function that moves an array item to a different position
 *
 * @param array
 * @param from
 * @param to
 *
 * @returns A copy of the input array with the item moved
 */
export function arrayMove<T>(array: T[], from: number, to: number) {
    const copy = [...array];
    moveElement(from, to, copy);
    return copy;
}

/**
 * Pure function that adds an element to an array
 * @returns New array with the new element inserted at the specified index
 */
export const pureInsert = <T>(array: T[], index: number, element: T) => [
    ...array.slice(0, index),
    element,
    ...array.slice(index)
];

/**
 * @function
 * @summary Moves an element from a given index to a given index
 *
 * @param sourceIndex
 * @param targetIndex
 * @param array
 */
export const moveElement = <T>(
    sourceIndex: number,
    targetIndex: number,
    array: T[]
) => {
    if (!isArray(array)) {
        throw new TypeError(`moveElement() array is not instance of Array`);
    }
    if (!Number.isInteger(sourceIndex) || sourceIndex < 0) {
        throw new TypeError(`moveElement() source index invalid`);
    }
    if (!Number.isInteger(targetIndex) || targetIndex < 0) {
        throw new TypeError(`moveElement() target index invalid`);
    }
    array.splice(targetIndex, 0, array.splice(sourceIndex, 1)[0]);
};

/**
 * @function
 * @summary Moves an element at a given index into an array
 *
 * @param element
 * @param targetIndex
 * @param array
 */
export const insertElement = <T>(
    element: T,
    targetIndex: number,
    array: T[]
) => {
    if (!isArray(array)) {
        throw new TypeError(`insertElement() array is not instance of Array`);
    }
    if (!Number.isInteger(targetIndex) || targetIndex < 0) {
        throw new TypeError(`insertElement() target index invalid`);
    }
    array.splice(targetIndex, 0, element);
};

/**
 * @function
 * @summary Removes an element at a given index in an array
 *
 * @param index
 * @param array
 */
export const removeElement = <T>(index: number, array: T[]) => {
    if (!isArray(array)) {
        throw new TypeError(`removeElement() array is not instance of Array`);
    }
    if (!Number.isInteger(index) || index < 0) {
        throw new TypeError(`removeElement() index invalid`);
    }
    array.splice(index, 1);
};

/**
 * @function
 * @summary A basic method to perform shallow equality check on two arrays
 * NOTE: Only works with arrays of basic scalar items
 *
 * @param arr1
 * @param arr2
 *
 * @returns true if arrays are equal
 */
export const compareArrays = <T>(arr1: T[], arr2: T[]): boolean => {
    if (!isArray(arr1) || !isArray(arr2)) return false;
    const arr2Sorted = arr2.slice().sort();
    return (
        arr1.length === arr2.length &&
        arr1
            .slice()
            .sort()
            .every((value, index) => {
                return value === arr2Sorted[index];
            })
    );
};

/**
 * @summary Returns a sorted array of objects given a type passed to the function.
 *
 * @param items - Array of items to be sorted.
 * @param property - The property in which we want to sort by.
 * @param descending - Whether we want to sort the list by descending or ascending order.
 *
 * @returns Sorted array
 */

export const sortArrayByType = <T extends object>(
    items: T[],
    property: string,
    { descending = true }: { descending?: boolean } = {}
): T[] => {
    const sortedList = items.concat().sort((a, b) => {
        const propA = a[property] && a[property].toString().toUpperCase();
        const propB = b[property] && b[property].toString().toUpperCase();
        if (propA < propB) {
            return -1;
        } else if (propA > propB) {
            return 1;
        } else return 0;
    });

    return descending === true ? sortedList : sortedList.reverse();
};
